import { Call, CallReason, ConnectionStatus, callEvents } from "@closerplatform/sdk/src";
import type { VideoContentType } from "@closerplatform/sdk/src/protocol/protocol";
import { merge, Observable, Subject } from "rxjs";
import { filter, first, map, mapTo, takeUntil } from "rxjs/operators";
import type { ArtichokeService } from "./artichokeService";
import { logger } from "../utils/logger";

export interface CallInfo {
  callId: string;
}

export interface UserCallInfo extends CallInfo {
  remoteUserId: string;
}

export interface CallConnectionInfo extends CallInfo {
  type: callEvents.ConnectionType;
}

export interface TrackInfo {
  kind: string;
  enabled?: boolean; // undefined means the status did not change
  track?: MediaStreamTrack;
  content?: VideoContentType;
}

/**
 * A service which handles all events connected to calls. There can be ony one call at a time.
 */
export abstract class AbstractCallService {
  protected readonly callEndedEvent = new Subject<CallInfo>();
  protected readonly callAnsweredEvent = new Subject<UserCallInfo>();
  protected readonly callConnectedEvent = new Subject<CallConnectionInfo>();
  protected readonly trackReceivedEvent = new Subject<TrackInfo>();

  private readonly internalCallHangupEvent = new Subject<CallInfo>();

  protected currentCall?: Call;
  protected currentLocalAudioTrack?: MediaStreamTrack;
  protected currentLocalVideoTrack?: MediaStreamTrack;

  constructor(protected artichokeService: ArtichokeService) {}

  protected abstract handleCallEnded(call: Call): void;

  public get ended$(): Observable<CallInfo> {
    return this.callEndedEvent;
  }

  public get answered$(): Observable<UserCallInfo> {
    return this.callAnsweredEvent;
  }

  public get connected$(): Observable<CallConnectionInfo> {
    return this.callConnectedEvent;
  }

  public get recordingEnabled(): boolean {
    return this.currentCall?.recordingEnabled ?? false;
  }

  public get trackReceived$(): Observable<TrackInfo> {
    return this.trackReceivedEvent;
  }

  public async reject(call: CallInfo): Promise<void> {
    if (this.currentCall && this.isRequestCallValid(call)) {
      try {
        await this.currentCall.reject(CallReason.CallRejected);
        this.internalCallHangupEvent.next(call);
      } catch (e) {
        logger.error({ message: "CallService: Error when rejecting the call", error: e });
      }
    }
  }

  public async leave(call: CallInfo): Promise<void> {
    if (this.currentCall && this.isRequestCallValid(call)) {
      try {
        await this.currentCall.leave(CallReason.Hangup);
        this.internalCallHangupEvent.next(call);
      } catch (e) {
        logger.error({ message: "CallService: Error when leaving the call", error: e });
      }
    }
  }

  public async setAudioStream(trackInfo: { enabled: boolean; track?: MediaStreamTrack }) {
    if (!this.currentCall) {
      return;
    }
    if (this.isNewAndValidAudioTrack(trackInfo.track)) {
      try {
        await this.currentCall.replaceTrackByKind(trackInfo.track);
      } catch (error) {
        logger.debug("Sending audio track", trackInfo.track);
        this.currentCall.addTrack(trackInfo.track);
      }
      this.currentLocalAudioTrack = trackInfo.track;
    }
    if (trackInfo.enabled !== undefined) {
      await this.currentCall.setAudioToggle(trackInfo.enabled, Date.now());
    }
  }

  public async setVideoStream(trackInfo: {
    enabled: boolean;
    track?: MediaStreamTrack;
    content?: VideoContentType;
  }) {
    if (!this.currentCall) {
      return;
    }
    if (this.isNewAndValidVideoTrack(trackInfo.track)) {
      try {
        await this.currentCall.replaceTrackByKind(trackInfo.track);
      } catch (error) {
        logger.debug("Sending audio track", trackInfo.track);
        this.currentCall.addTrack(trackInfo.track);
      }
      this.currentLocalVideoTrack = trackInfo.track;
    }
    if (trackInfo.enabled !== undefined || trackInfo.content) {
      await this.currentCall.setVideoToggle(trackInfo.enabled, Date.now(), trackInfo.content);
    }
  }

  protected handleNewCall = (ratelCall: Call) => {
    this.currentCall = ratelCall;

    // subscribe to all events which can end the call
    merge(
      this.currentCall.end$,
      this.currentCall.rejected$,
      this.currentCall.left$,
      this.currentCall.activeDevice$,
      this.currentCall.offline$,
      this.currentCall.peerStatus$.pipe(
        filter(({ status }) => status === ConnectionStatus.Failed),
        mapTo({ callId: ratelCall.id })
      ),
      this.artichokeService.disconnectedEvent.pipe(mapTo({ callId: ratelCall.id })),
      this.internalCallHangupEvent
    )
      .pipe(first())
      .subscribe((call) => this.finalizeCall(call));

    // subscribe to all events which change the media stream tracks
    merge(
      this.currentCall.remoteTrack$.pipe(
        map((remoteTrack) => ({ kind: remoteTrack.track.kind, track: remoteTrack.track }))
      ),
      this.currentCall.audioToggle$.pipe(
        map((toggled) => ({ kind: "audio", enabled: toggled.enabled }))
      ),
      this.currentCall.videoToggle$.pipe(
        map(
          (toggled) =>
            ({ kind: "video", enabled: toggled.enabled, content: toggled.content } as TrackInfo)
        )
      )
    )
      .pipe(takeUntil(this.callEndedEvent))
      .subscribe((trackInfo) => this.trackReceivedEvent.next(trackInfo));

    this.currentCall.answered$
      .pipe(takeUntil(this.callEndedEvent))
      .subscribe((answered) =>
        this.callAnsweredEvent.next({ callId: answered.callId, remoteUserId: answered.authorId })
      );

    this.currentCall.connected$.pipe(takeUntil(this.callEndedEvent)).subscribe((connected) => {
      logger.debug("CallService: Connected event received", connected);
      this.callConnectedEvent.next({
        callId: connected.callId,
        type: connected.type
      });
    });
  };

  private finalizeCall = (callInfo: CallInfo) => {
    this.withCurrentCall(callInfo, (call) => {
      this.handleCallEnded(call);
      this.callEndedEvent.next(callInfo);
      this.currentCall = undefined;
    });
  };

  protected isNewAndValidAudioTrack = (track?: MediaStreamTrack): track is MediaStreamTrack =>
    !!track &&
    track.kind === "audio" &&
    (!this.currentLocalAudioTrack || this.currentLocalAudioTrack.id !== track.id);

  protected isNewAndValidVideoTrack = (track?: MediaStreamTrack): track is MediaStreamTrack =>
    !!track &&
    track.kind === "video" &&
    (!this.currentLocalVideoTrack || this.currentLocalVideoTrack.id !== track.id);

  protected withCurrentCall = (requestCall: CallInfo, callback: (currentCall: Call) => void) => {
    if (this.currentCall && this.isRequestCallValid(requestCall)) {
      callback(this.currentCall);
    } else {
      logger.debug("CallService: ignored operation on wrong or missing current call");
    }
  };

  /**
   * Check if there is a call in progress and if the request is for the current call
   */
  protected isRequestCallValid = (call: CallInfo): boolean =>
    !!this.currentCall && call.callId === this.currentCall.id;
}
